import { prefersReducedMotion, yieldToMainThread } from '@theme/utilities';

/**
 * <stuburt-reveal> — one-shot, IntersectionObserver-driven scroll reveal.
 *
 * A restrained, premium "layers fade in as they arrive" effect. Opt-in via markup.
 * NEVER wrap an LCP / above-the-fold element, or any block containing
 * price / stock / variant / shipping / a primary CTA (see docs/motion-system-proposal.md §6).
 *
 * Attributes (all optional, set in Liquid):
 *   data-reveal="fade-up"        fade + small upward nudge (default)
 *   data-reveal="fade"           opacity only, no movement
 *   data-reveal-cascade          stagger the DIRECT CHILDREN so layers arrive in sequence
 *
 * Re-arming on editor re-render is automatic: the Section Rendering API re-inserts the
 * node, which re-fires connectedCallback. Do NOT add a shopify:section:load listener —
 * it would double-arm the observer.
 *
 * Fail-visible: the hidden start state is keyed on [data-reveal-pending], which only JS
 * sets — so no-JS, reduced-motion, and design-mode all render at full opacity.
 */
class StuburtReveal extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** Elements that actually animate: the children (cascade) or this element. */
  get #targets() {
    if (this.hasAttribute('data-reveal-cascade')) {
      // Cap the cascade so a long list never becomes a parade.
      return /** @type {HTMLElement[]} */ ([...this.children]).slice(0, 8);
    }
    return [/** @type {HTMLElement} */ (this)];
  }

  connectedCallback() {
    // Reduced-motion and the theme editor jump straight to the final state — a merchant
    // must never drag or duplicate invisible content, and motion-sensitive users get none.
    if (prefersReducedMotion() || window.Shopify?.designMode) {
      this.#showInstant();
      return;
    }
    this.#arm();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #arm() {
    const targets = this.#targets;
    if (targets.length === 0) return;

    // Mark the hidden start state (CSS only hides elements carrying this JS-set attribute).
    for (const el of targets) el.setAttribute('data-reveal-pending', '');

    const cascade = this.hasAttribute('data-reveal-cascade');

    this.#observer = new IntersectionObserver(
      (entries) => {
        // Index within the *intersecting batch*, not DOM order, so an already-visible
        // mid-page element never waits behind earlier siblings on a deep-link.
        const arriving = entries.filter((entry) => entry.isIntersecting);
        const step = cascade ? Math.min(150, Math.floor(700 / Math.max(arriving.length, 1))) : 0;

        arriving.forEach((entry, index) => {
          const el = /** @type {HTMLElement} */ (entry.target);

          if (cascade) {
            // Hard-cap the last item's start so the whole group still settles quickly.
            const delay = Math.min(index * step, 600);
            el.style.setProperty('--stuburt-reveal-delay', `${delay}ms`);
          }

          // Yield so the visible attribute lands after layout settles (jumbo-text pattern).
          yieldToMainThread().then(() => {
            el.setAttribute('data-reveal-visible', '');
            el.removeAttribute('data-reveal-pending');
          });

          this.#observer?.unobserve(el); // one-shot — reveal once, never re-hide
        });
      },
      // Pre-trigger ~12% of a viewport BELOW the fold so the fade is already settling
      // by the time the element scrolls into view — avoids the "blank, then slow fade-in"
      // feeling. threshold 0.01 = fire as soon as any sliver enters the expanded root.
      { rootMargin: '0px 0px 12% 0px', threshold: 0.01 }
    );

    for (const el of targets) this.#observer.observe(el);
  }

  #showInstant() {
    for (const el of this.#targets) {
      el.setAttribute('data-reveal-instant', '');
      el.removeAttribute('data-reveal-pending');
    }
  }
}

if (!customElements.get('stuburt-reveal')) {
  customElements.define('stuburt-reveal', StuburtReveal);
}
